import {
  accountQueryKey,
  type Organization,
  PlanType,
  RoleType,
  userAccountApi,
  userOrganizationsApi
} from 'api-validations/user-api'
import { type ApiClient, useApiClient, useQuery } from 'shared-ui/api-provider'
import { useAuth } from 'shared-ui/auth-provider'
import { ApiError, Async, isNil, partition } from 'utils'

import { useLocalization } from '../localization'

export const DEMO_ORG_CODE = '__demo__'

export function isActivePlan(
  org: Pick<Organization.UserOrganization, 'planType' | 'planTypeExpiresAt'>
) {
  return (
    org.planType === PlanType.Standard &&
    (isNil(org.planTypeExpiresAt) || org.planTypeExpiresAt > new Date())
  )
}

/**
 * A transitory org was created but never paid for (plan is None) and never touched since
 * creation (createdAt === updatedAt) — e.g. an operation a user started but abandoned
 * before subscribing. These are hidden from operation lists/switchers, but remain
 * resolvable by org lookups (see `allOrgMemberships`).
 */
function isTransitoryOrg(
  org: Pick<Organization.UserOrganization, 'planType' | 'createdAt' | 'updatedAt'>
) {
  return org.planType === PlanType.None && org.createdAt.getTime() === org.updatedAt.getTime()
}

const ROLE_ORDER = [RoleType.Owner, RoleType.Manager, RoleType.Member, RoleType.Observer]
const roleOrder = (role: RoleType) => {
  const i = ROLE_ORDER.indexOf(role)
  return i === -1 ? ROLE_ORDER.length : i
}

const queryFn = async (client: ApiClient, username: string, demoOrgName: string) => {
  const accountRequest = client
    .callApi(userAccountApi, { username }, undefined)
    .then((d) => d.items[0])
  const membershipsRequest = client
    .callApi(userOrganizationsApi, { username }, undefined)
    .then((d) => d.items)
    .catch((e) => {
      if (e instanceof ApiError && e.status === 404) return [] as Organization.UserOrganization[]
      throw e
    })
  const { account, allMemberships } = await Async.all({
    account: accountRequest,
    allMemberships: membershipsRequest
  })

  const isNewUser = allMemberships.length === 0

  // every user can access the demo operation, but it's at the end of their list (even after sorting)
  allMemberships.push({
    orgName: demoOrgName,
    orgCode: DEMO_ORG_CODE,
    ownerEmail: 'support@panorama.ag',
    supported: true,
    planType: PlanType.Standard,
    createdAt: new Date(),
    updatedAt: new Date(),
    userRoleType: RoleType.Member,
    ownerDisplayName: 'Panorama',
    llmDataSharingDisabled: false
  })

  const [supporterMemberships, orgMemberships] = partition(
    allMemberships,
    (o) => o.userRoleType === RoleType.Supporter
  )

  // the first org must not be one that the user's account is a Supporter at
  const firstNonSupportingOrg = orgMemberships[0]

  // Sort once: by role, then active-plan, then name. Both the full set (used for org
  // lookups) and the filtered display list below share this ordering.
  const sortedOrgMemberships = orgMemberships.sort((a, b) => {
    const roleDiff = roleOrder(a.userRoleType) - roleOrder(b.userRoleType)
    if (roleDiff !== 0) return roleDiff
    const planDiff = (isActivePlan(a) ? 0 : 1) - (isActivePlan(b) ? 0 : 1)
    if (planDiff !== 0) return planDiff
    return a.orgName.localeCompare(b.orgName)
  })

  return {
    ...account,
    defaultOperation: firstNonSupportingOrg,
    isNewUser,
    // The full membership set, including transitory orgs. Org lookups (e.g. useOrgInfo)
    // must be able to resolve a just-created, unpaid operation — such as the
    // subscribe-with-code page reached right after creation — so they read this rather
    // than the filtered display list below.
    allOrgMemberships: sortedOrgMemberships,
    // The display list for operation switchers/lists, with transitory orgs excluded.
    orgMemberships: sortedOrgMemberships.filter((o) => !isTransitoryOrg(o)),
    supporterMemberships: supporterMemberships.sort((a, b) => a.orgName.localeCompare(b.orgName))
  }
}

export type Operation = Result['defaultOperation']

type Result = Awaited<ReturnType<typeof queryFn>>

export const useAccount = () => {
  const username = useAuth().user?.username ?? ''
  const apiClient = useApiClient()
  const { t } = useLocalization()
  const demoOrgName = t(($) => $.titles.demoOperation)

  return useQuery({
    queryKey: [accountQueryKey],
    queryFn: () => queryFn(apiClient, username, demoOrgName),
    enabled: Boolean(username)
  })
}

type Account = Awaited<ReturnType<typeof queryFn>>

export const PanoramaWidePermissions = {
  isAdmin: (a: Account) => a.admin,
  isSupporter: (a: Account) => a.support
}
